import { readonly, ref } from "vue";

/**
 * The projects this session knows to be IMPORTED: known on this server only from releases
 * exported elsewhere (`source === "imported"`), with no source here to open — so the Designer
 * shows them read-only, their releases and nothing else.
 *
 * Learned two ways. A project read says so (`ProjectResponse.source`), and any request that
 * would open one is refused with `409 project_imported`, which `@/api` reports here whichever
 * call met it — the way `useLicenseGate` hears `license_required`. So the Designer's layout can
 * turn every page of such a project into its read-only home, instead of each view failing on a
 * refusal of its own.
 *
 * In `shared/` for the reason `useLicenseGate` is: the infra that hears the refusal may not
 * import a section, and it is free of imports so `@/api` can set it without a cycle. A project
 * never stops being imported (nothing on this server can give it a source), so nothing is
 * forgotten.
 */
const imported = ref<ReadonlySet<string>>(new Set());

/** `id` is an imported project. */
export function markProjectImported(id: string): void {
	if (!id || imported.value.has(id)) return;
	imported.value = new Set([...imported.value, id]);
}

/**
 * The project a refused API path names: `/projects/{id}/…` → `id`. Null for any other path —
 * only a project route can be refused for being imported.
 */
export function projectIdOfPath(path: string): string | null {
	const match = /^\/projects\/([^/?#]+)/.exec(path);
	return match ? decodeURIComponent(match[1]) : null;
}

export function useImportedProjects() {
	return {
		imported: readonly(imported),
		isImported: (id: string | null | undefined) => !!id && imported.value.has(id),
	};
}
